import React from 'react';
import { Link } from 'react-router';
import { Helmet } from 'react-helmet';
import SEOHead from '../../components/seo/seo-head';
import { SEO_CONFIG } from '../../constants/seo-config';
import Analytics from '../../services/analytics';
import AuthMixin from '../../mixins/authenticationMixin';
import AppStore from '../../stores/stores';
import AppRoutes from '../../routes/routes';
import DataError from '../../components/errors/data-error';
import LocalLoading from '../../components/loading/local-loading';
import BookmarkButtonMarketing from '../../pages/checklists/bookmark-button-marketing';
import ChatButtonMarketing from '../../pages/checklists/chat-button-marketing';
import ChecklistSectionMarketing from '../../pages/checklists/checklist-section-marketing';
import ChecklistContextMarketing from '../../components/checklists/checklist-context-marketing';
import ChecklistSidebarMarketing from '../../pages/checklists/checklist-sidebar-marketing';
import { SITE_URL } from '../../constants/seo-config';
import { isFeatureEnabled } from '../../constants/features';
import { checklistDescription } from '../../utils/checklist-description';

import './marketing-checklist-page.scss';

// The path the app was first loaded on, before any in-app navigation, and the title the
// server rendered for it
const INITIAL_PATH = window.location.pathname;
const INITIAL_TITLE = document.title;

class MarketingChecklistPage extends React.Component {
  constructor() {
    super();
    this.state = {
      title: '',
      last_updated: '',
      titleFootnotes: [],
      sections: [],
      signposts: [],
      topic: '',
      chapter: '',
      category: '',
      related: [],
      bookmarked: false,
      show_notes: false,
      loading: true,
      error: false,
    };
  }

  componentDidMount() {
    setTimeout(() => window.scrollTo(0, 0));

    this.loadChecklist();
  }

  loadChecklist() {
    const checklistId = this.props.params && this.props.params.id;
    if (!checklistId) {
      this.setState({ error: true, loading: false });
      return;
    }

    AppStore.getChecklist(checklistId, this.props.userApiRequest, {
      onSuccess: ({ data: checklist }) => {
        this.setState({
          title: checklist.data.title,
          last_updated: checklist.data.updated_at,
          titleFootnotes: checklist.data.title_footnotes || [],
          sections: checklist.data.sections || [],
          signposts: checklist.data.signposts || [],
          topic: checklist.topic,
          chapter: checklist.chapter,
          category: checklist.part,
          related: checklist.related || [],
          bookmarked: checklist.bookmarked,
          show_notes: checklist.data.title,
          loading: false,
        });
        Analytics.event('view_checklist', { checklist_slug: checklistId });
      },
      onError: ({ response } = {}) => {
        // A checklist renamed or retired since it was linked (old bookmarks, for example).
        // Load the URL from the server, which redirects old checklist URLs to their
        // replacement or shows its 404 page. Only after in-app navigation, so a full
        // page load can't loop.
        if (response && response.statusCode === 404 && window.location.pathname !== INITIAL_PATH) {
          window.location.replace(window.location.pathname + window.location.search);
          return;
        }
        this.setState({ error: true, loading: false });
      },
    });
  }

  getChecklistSEO() {
    const checklistConfig = SEO_CONFIG.checklist;
    const checklistId = this.props.params && this.props.params.id;
    const title = this.state.title || 'Neurology Checklist';
    const canonical = checklistId ? `${SITE_URL}/explore/checklists/${checklistId}` : `${SITE_URL}/explore`;
    const description = checklistDescription(title, this.state.sections);

    return {
      title: checklistConfig.titleTemplate(title),
      description,
      canonical,
      // JSON-LD for checklists comes from the server render (server/seo/checklist.js)
      ogType: checklistConfig.ogType
    };
  }

  render() {
    const { loading, error, title, last_updated, titleFootnotes, sections, topic, chapter, category, related, bookmarked } = this.state;

    if (loading) {
      return (
        <div className="marketing-checklist-page">
          {/* keep the server's title rather than the app's default while the checklist loads */}
          {window.location.pathname === INITIAL_PATH && <Helmet><title>{INITIAL_TITLE}</title></Helmet>}
          <LocalLoading text="Loading checklist..." />
        </div>
      );
    }

    if (error) {
      // A failed request here is usually temporary, and Google's renderer sometimes abandons
      // requests: no noindex, and on the page the server rendered, keep the server's title and
      // description so a crawler that hit the error still indexes the real checklist. Missing
      // checklists get a real 404 from the server.
      const onServerRenderedPage = window.location.pathname === INITIAL_PATH;
      return (
        <div className="marketing-checklist-page">
          {onServerRenderedPage
            ? <Helmet><title>{INITIAL_TITLE}</title></Helmet>
            : <Helmet><title>Checklist unavailable | Neurochecklists</title></Helmet>}
          <DataError />
        </div>
      );
    }

    const checklistSEO = this.getChecklistSEO();

    return (
      <div className="marketing-checklist-page">
        <SEOHead {...checklistSEO} />

        <section className="m-checklist-header">
          <div className="checklist-header-content">
            <div className="breadcrumb">
              <Link to={AppRoutes.explore}>Explore</Link>
              {category && (
                <>
                  <span className="separator">/</span>
                  <span>{category.title || category}</span>
                </>
              )}
              {chapter && (
                <>
                  <span className="separator">/</span>
                  <span>{chapter.title || chapter}</span>
                </>
              )}
              {topic && (
                <>
                  <span className="separator">/</span>
                  <span>{topic.title || topic}</span>
                </>
              )}
            </div>

            <div className="header-top">
              <div>
                <h1>{title}</h1>
                <p className="last-updated">
                  Written and reviewed by {isFeatureEnabled('authorPage')
                    ? <Link to={AppRoutes.ibrahimImam} className="reviewer">Dr Ibrahim Imam</Link>
                    : 'Dr Ibrahim Imam'}, Consultant Neurologist
                  {last_updated && (
                    <> · Last reviewed <time className="date" dateTime={last_updated}>{new Date(last_updated).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</time></>
                  )}
                </p>
              </div>
              <div className="header-actions">
                <ChatButtonMarketing title={title} userApiRequest={this.props.userApiRequest} />
                <BookmarkButtonMarketing bookmarked={bookmarked} title={title} userApiRequest={this.props.userApiRequest} />
              </div>
            </div>
          </div>
        </section>

        <section className="checklist-content">
          <div className="checklist-grid-container">
            {/* Left Sidebar */}
            <aside className="checklist-left-sidebar">
              <ChecklistContextMarketing {...this.props} current={this.props.params && this.props.params.id} chapter={chapter} topic={topic} related={related} />
            </aside>

            {/* Main Article Content */}
            <article className="checklist-main-content">
              {sections && sections.length > 0 ? (
                <div className="sections-list">
                  {sections.map((section, idx) => (
                    <ChecklistSectionMarketing
                      key={idx}
                      id={idx}
                      title={section.title}
                      list={section.list_items}
                    />
                  ))}
                  {this.state.signposts && this.state.signposts.length > 0 && (
                    <ChecklistSectionMarketing
                      key="signposts"
                      title={{ text: 'References' }}
                      list={{ data: this.state.signposts }}
                      signposts={true}
                      query={this.props.location ? this.props.location.query : null}
                    />
                  )}
                </div>
              ) : (
                <p className="no-content">No checklist items available.</p>
              )}
            </article>

            {/* Right Sidebar */}
            <aside className="checklist-right-sidebar">
              <ChecklistSidebarMarketing {...this.props} {...this.state} />
            </aside>
          </div>
        </section>
      </div>
    );
  }
}

export default AuthMixin(MarketingChecklistPage);
